import { useState, useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import Tweakpane from 'tweakpane';
import shallow from 'zustand/shallow';
import produce from 'immer';
import create from 'zustand';

const useStore = create(set => ({
  // @ts-expect-error
  setValue: fn => set(produce(fn))
})); // @ts-expect-error

const setValue = (id, key, value) => useStore.getState().setValue(state => {
  if (typeof state[id] === "undefined") {
    state[id] = {};
  }

  state[id][key] = value;
});

function uuid() {
  return "" + Math.floor(new Date().getTime() * Math.random() / 1000);
}

let SpecialInputTypes;

(function (SpecialInputTypes) {
  SpecialInputTypes[SpecialInputTypes["SEPARATOR"] = 0] = "SEPARATOR";
  SpecialInputTypes[SpecialInputTypes["DIRECTORY"] = 1] = "DIRECTORY";
  SpecialInputTypes[SpecialInputTypes["BUTTON"] = 2] = "BUTTON";
  SpecialInputTypes[SpecialInputTypes["MONITOR"] = 3] = "MONITOR";
})(SpecialInputTypes || (SpecialInputTypes = {}));

function makeSeparator() {
  return {
    ["_" + uuid()]: {
      type: SpecialInputTypes.SEPARATOR
    }
  };
}
function makeFolder(title, schema, expanded = true) {
  return {
    ["_" + uuid()]: {
      type: SpecialInputTypes.DIRECTORY,
      title,
      schema,
      settings: {
        expanded
      }
    }
  };
}
const makeDirectory = makeFolder;
function makeButton(title, onClick) {
  return {
    ["_" + uuid()]: {
      type: SpecialInputTypes.BUTTON,
      title,
      onClick
    }
  };
}
function makeMonitor(key, settings = {}) {
  return {
    set: value => OBJECT[key] = value,
    get: () => ({
      ["_" + uuid()]: {
        type: SpecialInputTypes.MONITOR,
        key,
        settings
      }
    })
  };
}

// The object that Tweakpane will mutate
const OBJECT = {}; // Will hold a reference to the Tweakpane instance

let pane;

function getInitialValues(schema) {
  return Object.entries(schema).reduce((values, [key, inputDefinition]) => {
    let inputVal = null;

    if (typeof inputDefinition === "object") {
      const {
        value,
        min,
        type
      } = inputDefinition; // if directory, get values from all inputs

      if (type === SpecialInputTypes.DIRECTORY) {
        return { ...values,
          ...getInitialValues(inputDefinition.schema)
        };
      } else {
        if (!(type in SpecialInputTypes)) {
          inputVal = value || min;
        }
      }
    } else {
      inputVal = inputDefinition;
    }

    return { ...values,
      [key]: inputVal
    };
  }, {});
}

function constructObjectAndState(id, OBJECT, pane, schema) {
  Object.entries(schema).forEach(([key, input]) => {
    // @ts-expect-error
    let inputVal = null;
    let settings = {};

    if (typeof input === "object") {
      const {
        type
      } = input;

      if (type === SpecialInputTypes.MONITOR) {
        const {
          key,
          settings
        } = input;
        OBJECT[key] = 0;
        pane.addMonitor(OBJECT, key, settings);
        return;
      }

      if (type === SpecialInputTypes.BUTTON) {
        const {
          title,
          onClick
        } = input;
        if (typeof onClick !== "function") throw new Error("Button onClick must be a function.");
        pane.addButton({
          title
        }).on("click", onClick);
        return;
      }

      if (type === SpecialInputTypes.SEPARATOR) {
        pane.addSeparator();
        return;
      }

      if (type === SpecialInputTypes.DIRECTORY) {
        const {
          title,
          schema,
          settings
        } = input;
        const folder = pane.addFolder({
          title,
          ...settings
        });
        constructObjectAndState(id, OBJECT, folder, schema);
        return;
      }

      const {
        value,
        ...sett
      } = input;
      inputVal = value || sett.min;
      settings = sett;
    } else {
      inputVal = input;
    } // assign initial value


    OBJECT[key] = inputVal; // set init values
    // @ts-expect-error

    setValue(id, key, inputVal); // onchange, set value in state

    pane.addInput(OBJECT, key, settings).on("change", value => {
      // @ts-expect-error
      setValue(id, key, value);
    });
  });
}

function useTweaks(nameOrSchema, schema, settings) {
  const _name = typeof nameOrSchema === "string" && nameOrSchema;

  const _schema = typeof nameOrSchema === "string" ? schema : nameOrSchema;

  const [id] = useState(uuid());
  const constructed = useRef(false);
  useLayoutEffect(() => {
    // initialize a new pane whene non is defined
    if (typeof pane === "undefined") {
      pane = new Tweakpane(settings);
    }
  }, []);
  useEffect(() => {
    if (!constructed.current) {
      if (_name) {
        const paneFolder = pane.addFolder({
          title: _name
        });
        constructObjectAndState(id, OBJECT, paneFolder, _schema);
      } else {
        constructObjectAndState(id, OBJECT, pane, _schema);
      }

      constructed.current = true;
    }
  }, [_name, _schema, id]); // Only update when values concering this particular instance are changed

  const valuesFromState = useStore(useCallback(state => state[id], [id]), shallow);
  return constructed.current ? valuesFromState : getInitialValues(_schema);
}

export { OBJECT, SpecialInputTypes, makeButton, makeDirectory, makeFolder, makeMonitor, makeSeparator, useTweaks };
